// src/components/Support/pptExports/pptTPPhelpers.js

export const TPP_ACTIVITY_STATUS_COLORS = {
    Planned: '3B99FC',
    Initiated: 'FF9C2B',
    'On Hold': '5A35D6',
    Cancelled: 'C30A30',
    Completed: '09CC9E',
    Active: '09CC9E',
    Ongoing: '09CC9E',
    Proposed: '3B99FC',
    Ideation: '9E9E9E',
    Uncategorized: '9E9E9E',
    'No Activities': 'D9D9D9',
}

export const TPP_ACTIVITY_STATUS_ORDER = ['Planned', 'Initiated', 'On Hold', 'Cancelled', 'Completed', 'Uncategorized']

export function cleanTppText(s) {
    return (s || '')
        .toString()
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
        .replace(/\r\n/g, '\n')
        .replace(/\n+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

export function normalizeTppActivityStatus(status) {
    const raw = cleanTppText(status)
    if (!raw) return 'Uncategorized'

    const lower = raw.toLowerCase()

    if (lower === 'planned') return 'Planned'
    if (lower === 'initiated') return 'Initiated'
    if (lower === 'on hold') return 'On Hold'
    if (lower === 'cancelled' || lower === 'canceled') return 'Cancelled'
    if (lower === 'completed') return 'Completed'
    if (lower === 'active' || lower === 'ongoing') return 'Completed'
    if (lower === 'proposed') return 'Planned'
    if (lower === 'ideation') return 'Uncategorized'

    return raw
}

export function getTppActivityTitle(activity) {
    return cleanTppText(
        activity?.title ||
            activity?.activityTitle ||
            activity?.activity_title ||
            activity?.name ||
            activity?.label ||
            'Untitled activity'
    )
}

export function getTppActivityStatus(activity) {
    return normalizeTppActivityStatus(activity?.status || activity?.activityStatus || activity?.activity_status)
}

export function countTppLines(text, charsPerLine = 30) {
    const t = cleanTppText(text)
    if (!t) return 1
    return Math.max(1, Math.ceil(t.length / charsPerLine))
}

export function getTppTotalHeight(rowHeights = []) {
    return rowHeights.reduce((sum, h) => sum + h, 0)
}

export function transformLiveTppDataForExport({ tpp, columns, sections, rows, slots, cells, assetName }) {
    const columnsById = new Map((columns || []).map((c) => [c._id.toString(), c]))
    const sectionsById = new Map((sections || []).map((s) => [s._id.toString(), s]))
    const rowsById = new Map((rows || []).map((r) => [r._id.toString(), r]))
    const slotsById = new Map((slots || []).map((s) => [s._id.toString(), s]))

    const cellLookup = {}

    for (const cell of cells || []) {
        const slotId = cell.slotId?.toString()
        const columnId = cell.columnId?.toString()

        if (!slotId || !columnId) continue

        if (!cellLookup[slotId]) cellLookup[slotId] = {}
        cellLookup[slotId][columnId] = cell
    }

    const orderedColumns = (tpp.columnIds || [])
        .map((id) => columnsById.get(id.toString()))
        .filter(Boolean)
        .filter((col) => !col.archived)
        .sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1
            if (!a.isPinned && b.isPinned) return 1
            return 0
        })

    const scenarios = orderedColumns.map((col, idx) => ({
        scenario_id: col._id.toString(),
        scenario_name: col.title,
        order: idx + 1,
    }))

    const categories = []

    for (const section of (tpp.sectionIds || []).map((id) => sectionsById.get(id.toString())).filter(Boolean)) {
        const attributes = []

        for (const row of (section.rowIds || []).map((id) => rowsById.get(id.toString())).filter(Boolean)) {
            const slotRows = []

            for (const slot of (row.slotIds || []).map((id) => slotsById.get(id.toString())).filter(Boolean)) {
                const scenarioCells = []

                for (const col of orderedColumns) {
                    const cell = cellLookup[slot._id.toString()]?.[col._id.toString()]

                    let displayContent = cell?.content || ''
                    let differentiating = !!cell?.isHighlighted
                    let sourceCell = null

                    if (cell?.isClone) {
                        const cloneId = cell._id?.toString()

                        sourceCell = (cells || []).find((possibleSource) =>
                            (possibleSource.childClones || []).map((id) => id?.toString()).includes(cloneId)
                        )

                        // TODO:
                        // We currently expand clones only when the clone text contains
                        // "Same as Reference". This assumes TPP clone labels follow the
                        // standard Base/Best/Asset/Reference naming convention.
                        // If clients can customize scenario names or clone labels,
                        // replace this text-based detection with a scenario-driven rule.

                        if (sourceCell) {
                            const cloneContent = cleanTppText(cell?.content || '')
                            const cloneContentLower = cloneContent.toLowerCase()

                            const shouldExpandClone = cloneContentLower.includes('same as reference')

                            displayContent = shouldExpandClone ? sourceCell.content || '' : cell.content || ''

                            differentiating = !!sourceCell.isHighlighted
                        }
                    }

                    const content = cleanTppText(displayContent)

                    if (!content) continue

                    scenarioCells.push({
                        scenario_id: col._id.toString(),
                        element_text: content,
                        cell_id: cell?._id?.toString(),
                        source_cell_id: sourceCell?._id?.toString(),
                        is_clone: !!cell?.isClone,
                        slot_id: slot._id.toString(),
                        row_id: row._id.toString(),
                        flags: {
                            differentiating,
                        },
                    })
                }

                slotRows.push({
                    row_id: slot._id.toString(),
                    slot_context: slot.context || '',
                    scenario_cells: scenarioCells,
                })
            }

            attributes.push({
                attribute_id: row._id.toString(),
                attribute_name: row.title,
                attribute_info: row.info || '',
                rows: slotRows,
            })
        }

        categories.push({
            category_id: section._id.toString(),
            category_name: section.title,
            attributes,
        })
    }

    return {
        trpp_module: {
            roadmap_id: tpp.roadmapId?.toString() || '',
            asset_name: assetName,
            scenarios,
            categories,
        },
    }
}

export function normalizeLiveTppForRows(trppData, { highlightedOnly = true } = {}) {
    const m = trppData?.trpp_module
    if (!m) return null

    return {
        assetName: m.asset_name || '',
        scenarios: (m.scenarios || [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((s) => ({
                id: s.scenario_id,
                name: s.scenario_name,
                order: s.order ?? 0,
            })),
        categories: (m.categories || []).map((c) => ({
            id: c.category_id,
            name: c.category_name,
            attributes: (c.attributes || []).map((a) => ({
                id: a.attribute_id,
                name: a.attribute_name,
                info: a.attribute_info || '',
                groups: (a.rows || []).map((r, slotIndex) => {
                    const cellsByScenarioId = {}

                    for (const sc of r.scenario_cells || []) {
                        const isHighlighted = !!sc.flags?.differentiating

                        if (highlightedOnly && !isHighlighted) continue

                        if (!cellsByScenarioId[sc.scenario_id]) cellsByScenarioId[sc.scenario_id] = []

                        cellsByScenarioId[sc.scenario_id].push({
                            text: sc.element_text,
                            isHighlighted,
                            cellId: sc.cell_id,
                            slotId: sc.slot_id || r.row_id,
                            rowId: sc.row_id,
                            slotIndex,
                        })
                    }

                    return {
                        rowId: r.row_id,
                        slotIndex,
                        slotContext: r.slot_context || '',
                        cellsByScenarioId,
                    }
                }),
            })),
        })),
    }
}

export function buildTppActivityMapFromElementSupportResponses(...responses) {
    const activityMap = {}

    responses.forEach((response) => {
        const groups = response?.data || []

        groups.forEach((group) => {
            ;(group.cells || []).forEach((cell) => {
                if (!cell?._id) return
                activityMap[cell._id.toString()] = cell.activities || []
            })
        })
    })

    return activityMap
}

export function buildTppFulfillmentRowsForCategory(category, baseScenarioId, bestScenarioId, activityMap = {}) {
    const rows = []

    for (const attr of category.attributes || []) {
        for (const group of attr.groups || []) {
            const baseElements = group.cellsByScenarioId?.[baseScenarioId] || []
            const bestElements = group.cellsByScenarioId?.[bestScenarioId] || []

            const maxLen = Math.max(baseElements.length, bestElements.length)
            if (maxLen === 0) continue

            for (let i = 0; i < maxLen; i++) {
                const baseElement = baseElements[i]
                const bestElement = bestElements[i]

                rows.push({
                    attributeName: attr.name,
                    slotIndex: group.slotIndex,
                    baseText: baseElement?.text || '',
                    baseHighlighted: !!baseElement?.isHighlighted,
                    baseActivities: baseElement?.cellId ? activityMap[baseElement.cellId] || [] : [],
                    bestText: bestElement?.text || '',
                    bestHighlighted: !!bestElement?.isHighlighted,
                    bestActivities: bestElement?.cellId ? activityMap[bestElement.cellId] || [] : [],
                })
            }
        }
    }

    return rows
}

export function buildTppActivityRuns(activities = [], opts = {}) {
    const { textColor = '000000', fontSize = 6.8, activityStatusStyles = {}, getStatusLabel = null } = opts

    if (!activities.length) {
        // return [
        //     {
        //         text: '• No linked activities',
        //         options: {
        //             color: '777777',
        //             italic: true,
        //             fontSize,
        //         },
        //     },
        // ]

        return ''
    }

    const runs = []

    activities.forEach((activity, idx) => {
        const statusId =
            typeof getStatusLabel === 'function' ? getStatusLabel(activity) : getTppActivityStatus(activity)

        const style = activityStatusStyles?.[statusId] ||
            activityStatusStyles?.Uncategorized || { fill: '999999', label: statusId || 'Uncategorized' }

        const label = style.label || statusId || 'Uncategorized'
        const color = style.fill || style.line || '999999'
        const title = getTppActivityTitle(activity)

        runs.push({
            text: `• [${label}] `,
            options: {
                color,
                bold: true,
                fontSize,
            },
        })

        runs.push({
            text: `${title}${idx < activities.length - 1 ? '\n' : ''}`,
            options: {
                color: textColor,
                fontSize,
            },
        })
    })

    return runs
}

export function buildTppElementRuns(text, isHighlighted = false, opts = {}) {
    const { green = '268500', textColor = '000000', fontSize = 7.2 } = opts
    const t = cleanTppText(text)

    if (!t) return ''

    return [
        {
            text: t,
            options: {
                color: isHighlighted ? green : textColor,
                bold: !!isHighlighted,
                fontSize,
            },
        },
    ]
}

export function estimateTppFulfillmentRowHeight(row, opts = {}) {
    const {
        baseRowH = 0.32,
        lineInc = 0.12,
        maxRowH = 1.05,
        attrChars = 18,
        elementChars = 34,
        activityChars = 26,
    } = opts

    const attrLines = countTppLines(row.attributeName, attrChars)
    const baseLines = countTppLines(row.baseText, elementChars)
    const bestLines = countTppLines(row.bestText, elementChars)

    const baseActLines = Math.max(
        1,
        (row.baseActivities || []).reduce((sum, a) => sum + countTppLines(getTppActivityTitle(a), activityChars), 0)
    )

    const bestActLines = Math.max(
        1,
        (row.bestActivities || []).reduce((sum, a) => sum + countTppLines(getTppActivityTitle(a), activityChars), 0)
    )

    const lines = Math.max(attrLines, baseLines, bestLines, baseActLines, bestActLines)

    const hasNoActivities = !(row.baseActivities || []).length && !(row.bestActivities || []).length
    const minRowH = hasNoActivities ? 0.38 : baseRowH

    return Math.min(maxRowH, Math.max(minRowH, minRowH + (lines - 1) * lineInc))
}

export function addTppActivityStatusLegend(slide, pptx, opts = {}) {
    const {
        x = 3.55,
        y = 0.1,
        w = 2.8,
        fontFace = 'Arial',
        textColor = '000000',
        rowH = 0.17,
        swatch = 0.07,
        padX = 0.08,
        padY = 0.06,
    } = opts

    slide.addShape(pptx.ShapeType.rect, {
        x,
        y,
        w,
        h: padY * 2 + 2 * rowH,
        fill: { color: 'FFFFFF' },
        line: { color: '000000', pt: 0.75 },
    })

    TPP_ACTIVITY_STATUS_ORDER.forEach((status, idx) => {
        const col = idx % 3
        const row = Math.floor(idx / 3)
        const cellW = w / 3
        const cx = x + padX + col * cellW
        const cy = y + padY + row * rowH
        const color = TPP_ACTIVITY_STATUS_COLORS[status] || TPP_ACTIVITY_STATUS_COLORS.Uncategorized

        slide.addShape(pptx.ShapeType.rect, {
            x: cx,
            y: cy + 0.045,
            w: swatch,
            h: swatch,
            fill: { color },
            line: { color, pt: 0.5 },
        })

        slide.addText(status, {
            x: cx + swatch + 0.04,
            y: cy,
            w: cellW - swatch - 0.08,
            h: rowH,
            fontFace,
            fontSize: 5.5,
            color: textColor,
            margin: 0,
            valign: 'mid',
        })
    })
}

export function addTppDifferentiationBox(slide, pptx, opts = {}) {
    const { slideW = 10.0, x, y = 0.1, w = 3.5, h = 0.45, fontFace = 'Arial', textColor = '000000' } = opts

    const boxX = x ?? slideW - w - 0.1

    slide.addShape(pptx.ShapeType.rect, {
        x: boxX,
        y,
        w,
        h,
        fill: { color: 'EAF7EA' },
        line: { color: 'B7E3B7', pt: 1 },
    })

    slide.addText('Green text represents elements that most significantly impact differentiation.', {
        x: boxX,
        y: y + 0.03,
        w,
        h: h - 0.1,
        fontFace,
        fontSize: 8,
        color: textColor,
        align: 'right',
        valign: 'top',
    })
}

export function getHighestTppActivityStatus(cell = {}, opts = {}) {
    const { getStatusLabel = null, activityStatusOrderMap = {} } = opts
    const activities = cell.activities || []

    if (!activities.length) return 'No Activities'

    let bestStatus = null
    let bestOrder = -Infinity

    activities.forEach((activity) => {
        const statusId =
            typeof getStatusLabel === 'function' ? getStatusLabel(activity) : getTppActivityStatus(activity)

        if (!statusId || statusId === 'Uncategorized') {
            return
        }

        const order = activityStatusOrderMap[statusId] ?? -Infinity

        if (order > bestOrder) {
            bestOrder = order
            bestStatus = statusId
        }
    })

    return bestStatus || 'No Status'
}

export function buildTppFulfillmentMetricData(apiData = [], opts = {}) {
    const { activityStatusStyles = {}, getStatusLabel = null, categoryOrder = [], activityStatusOrderMap = {} } = opts

    const rows = (apiData || []).map((group) => {
        const row = {
            category: group.grouping || 'Uncategorized',
            total: group.cells?.length || 0,
            'No Activities': 0,
            'No Status': 0,
        }

        Object.keys(activityStatusStyles || {}).forEach((statusId) => {
            row[statusId] = 0
        })
        ;(group.cells || []).forEach((cell) => {
            const status = getHighestTppActivityStatus(cell, {
                getStatusLabel,
                activityStatusOrderMap,
            })

            if (row[status] == null) row[status] = 0
            row[status] += 1
        })

        return row
    })

    const orderMap = new Map((categoryOrder || []).map((name, idx) => [name, idx]))

    return rows.sort((a, b) => {
        if (a.category === 'All') return -1
        if (b.category === 'All') return 1

        const aOrder = orderMap.has(a.category) ? orderMap.get(a.category) : 999
        const bOrder = orderMap.has(b.category) ? orderMap.get(b.category) : 999

        if (aOrder !== bOrder) return aOrder - bOrder
        return a.category.localeCompare(b.category)
    })
}

export function addTppFulfillmentMetricsSlide(pptx, opts = {}) {
    const {
        title = 'TrPP Fulfillment Metrics',
        subtitle = '',
        baseMetricData = [],
        bestMetricData = [],
        slideWidth = 10.0,
        backgroundColor = 'FFFFFF',
        titleColor = '000000',
        activityStatusStyles = {},
        createActivityStatusLegend = null,
        masterName = 'CONTENT_SLIDE',
    } = opts

    const slide = pptx.addSlide({ masterName })
    slide.background = { color: backgroundColor }

    const legendStyles = {
        ...activityStatusStyles,
        'No Status': {
            fill: 'BFBFBF',
            line: 'BFBFBF',
            label: 'No Status',
        },
        'No Activities': {
            fill: 'D9D9D9',
            line: 'D9D9D9',
            label: 'No Activities',
        },
    }

    const statusIds = Object.keys(legendStyles)

    function getStatusColor(statusId) {
        return legendStyles?.[statusId]?.fill || legendStyles?.[statusId]?.line || '999999'
    }

    function getStatusLabelForLegend(statusId) {
        return legendStyles?.[statusId]?.label || statusId
    }

    if (typeof createActivityStatusLegend === 'function') {
        createActivityStatusLegend(pptx, slide, legendStyles, slideWidth, null, 0.14, {
            maxCols: null,
            minCols: 3,
            maxColsCap: 4,
            cellW: 1.0,
            cellH: 0.15,
            swatch: 0.075,
            baseFont: 6,
            denseFont: 6,
            rightMargin: 0.15,
            align: 'right',
        })
    }

    slide.addText(title, {
        x: 0.5,
        y: 0.88,
        w: slideWidth - 1.0,
        h: 0.3,
        fontSize: 17,
        bold: true,
        align: 'center',
        color: titleColor,
        margin: 0,
    })

    function addDonut(row, pos, isAll = false) {
        const values = statusIds.map((statusId) => row?.[statusId] || 0)
        const labels = statusIds.map(getStatusLabelForLegend)
        const colors = statusIds.map(getStatusColor)
        const total = row?.total || values.reduce((sum, v) => sum + v, 0)

        if (total > 0) {
            slide.addChart(
                pptx.ChartType.doughnut,
                [
                    {
                        name: row.category,
                        labels,
                        values,
                    },
                ],
                {
                    x: pos.x,
                    y: pos.y,
                    w: pos.w,
                    h: pos.h,
                    holeSize: 58,
                    chartColors: colors,
                    firstSliceAng: 270,
                    border: { color: 'FFFFFF', pt: 0.5 },
                    showLegend: false,
                    showValue: false,
                    showCategoryName: false,
                    showPercent: false,
                    showLeaderLines: false,
                }
            )

            slide.addText(String(total), {
                x: pos.x + pos.w * 0.38,
                y: pos.y + pos.h * 0.39,
                w: pos.w * 0.24,
                h: 0.22,
                fontSize: isAll ? 15 : 11,
                bold: true,
                align: 'center',
                color: '000000',
                margin: 0,
            })
        } else {
            slide.addText('No elements', {
                x: pos.x,
                y: pos.y + pos.h * 0.45,
                w: pos.w,
                h: 0.25,
                fontSize: 8,
                italic: true,
                align: 'center',
                color: '666666',
                margin: 0,
            })
        }

        slide.addText(row.category, {
            x: pos.x,
            y: pos.y + pos.h - 0.05,
            w: pos.w,
            h: 0.18,
            fontSize: isAll ? 10 : 9, // Font size for the All Category is slightly larger than the category labels
            bold: true,
            align: 'center',
            color: '000000',
            margin: 0,
        })
    }

    // TODO: Improve metric layout responsiveness.
    // Current layout supports up to ~6 categories per Base/Best band in one row.
    // If category count grows beyond 5–6, consider splitting Base Case and Best Case
    // into separate slides or wrapping category donuts to a second row.

    function addMetricBand(label, metricData, y) {
        const allRow = metricData.find((row) => row.category === 'All') || {
            category: 'All',
            total: 0,
        }

        const categoryRows = metricData.filter((row) => row.category !== 'All')

        slide.addText(label, {
            x: 0.22,
            y,
            w: 2.0,
            h: 0.18,
            fontSize: 9,
            bold: true,
            color: '000000',
            margin: 0,
        })

        addDonut(
            allRow,
            {
                x: 0.18,
                y: y + 0.18,
                w: 1.75,
                h: 1.45,
            },
            true
        )

        const chartW = categoryRows.length <= 4 ? 1.58 : 1.34
        const chartH = categoryRows.length <= 4 ? 1.28 : 1.12
        const startX = 2.22
        const startY = y + 0.2
        const gapX = 0.08

        categoryRows.slice(0, 6).forEach((row, idx) => {
            addDonut(
                row,
                {
                    x: startX + idx * (chartW + gapX),
                    y: startY,
                    w: chartW,
                    h: chartH,
                },
                false
            )
        })
    }

    addMetricBand('Base Case', baseMetricData, 1.18)

    slide.addShape(pptx.ShapeType.line, {
        x: 0.25,
        y: 3.08,
        w: 9.5,
        h: 0,
        line: { color: 'D9D9D9', pt: 1 },
    })

    addMetricBand('Best Case', bestMetricData, 3.22)

    return slide
}

export function enrichTppDataForExecSummary(trppData, { tppRes, rows, slots } = {}) {
    const rowInfoById = new Map((rows || []).map((r) => [r._id.toString(), r.info || '']))
    const slotContextById = new Map((slots || []).map((s) => [s._id.toString(), s.context || '']))

    const m = trppData?.trpp_module
    if (!m) return trppData

    m.asset_ambition = (tppRes?.data?.assetAmbition || '').toString()

    for (const cat of m.categories || []) {
        for (const att of cat.attributes || []) {
            att.attribute_info = rowInfoById.get(att.attribute_id?.toString()) || ''
            for (const row of att.rows || []) {
                row.slot_context = slotContextById.get(row.row_id?.toString()) || ''
            }
        }
    }

    return trppData
}

export function normalizeTppForStandardExport(trppData, options = {}) {
    const { highlightedOnly = false, includeSlotContext = false, includeAttributeInfo = false } = options

    const m = trppData?.trpp_module
    if (!m) return null

    const scenarios = (m.scenarios || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((s) => ({
            id: s.scenario_id,
            name: s.scenario_name,
            order: s.order ?? 0,
        }))

    const categories = (m.categories || []).map((c) => {
        const attributes = (c.attributes || []).map((a) => {
            const groups = []
            const bulletsByScenarioId = {}

            for (const row of a.rows || []) {
                const group = {
                    rowId: row?.row_id || '',
                    slotContext: includeSlotContext ? row?.slot_context || '' : '',
                    bulletsByScenarioId: {},
                }

                for (const sc of row.scenario_cells || []) {
                    const sid = sc.scenario_id
                    if (!sid) continue

                    const text = cleanTppText(sc.element_text || '')
                    if (!text) continue

                    const isHighlighted = !!sc.isHighlighted || !!sc.flags?.differentiating
                    if (highlightedOnly && !isHighlighted) continue

                    if (!group.bulletsByScenarioId[sid]) group.bulletsByScenarioId[sid] = []
                    group.bulletsByScenarioId[sid].push({
                        text,
                        isHighlighted,
                        cellId: sc.cell_id || '',
                    })

                    if (!bulletsByScenarioId[sid]) bulletsByScenarioId[sid] = []
                    bulletsByScenarioId[sid].push({
                        text,
                        isHighlighted,
                        cellId: sc.cell_id || '',
                    })
                }

                groups.push(group)
            }

            return {
                id: a.attribute_id,
                name: a.attribute_name,
                info: includeAttributeInfo ? a.attribute_info || '' : '',
                groups,
                bulletsByScenarioId,
            }
        })

        return {
            id: c.category_id,
            name: c.category_name,
            attributes,
        }
    })

    return {
        assetName: m.asset_name || '',
        assetAmbition: (m.asset_ambition || '').toString(),
        scenarios,
        categories,
    }
}

export function stripTppControlChars(s) {
    return (s || '').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
}

export function cleanTppOneLine(s) {
    return stripTppControlChars((s || '').toString())
        .replace(/\r\n/g, '\n')
        .replace(/\n+/g, ' ')
        .trim()
}

export function getTppTotalRowHeight(rowHeights = []) {
    return rowHeights.reduce((sum, h) => sum + h, 0)
}

export function countTppCharsForRuns(runs) {
    if (!runs) return 0
    if (!Array.isArray(runs)) return String(runs || '').length
    return runs.reduce((sum, r) => sum + (r?.text || '').length, 0)
}

export function tppGroupHasAnyBullets(group, scenarios) {
    return scenarios.some((s) => (group.bulletsByScenarioId?.[s.id] || []).length > 0)
}

export function buildTppBulletRuns(
    bullets,
    { forceColor = null, forceBold = null, fontSize = null, highlightColor = '268500', normalTextColor = '000000' } = {}
) {
    const items = bullets || []
    const out = []

    for (let i = 0; i < items.length; i++) {
        const b = items[i]
        const t = cleanTppOneLine(b?.text)
        if (!t) continue

        out.push({
            text: `• ${t}${i < items.length - 1 ? ' ' : ''}`,
            options: {
                color: forceColor ?? (b?.isHighlighted ? highlightColor : normalTextColor),
                bold: forceBold ?? !!b?.isHighlighted,
                ...(fontSize != null ? { fontSize } : {}),
            },
        })
    }

    return out
}

export function estimateTppHeightFromRuns(
    runs,
    { charsPerLine = 34, baseRowH = 0.42, lineInc = 0.16, minH = 0.38, maxH = 4.0 } = {}
) {
    if (!runs) return minH
    if (!Array.isArray(runs)) runs = [{ text: String(runs || '') }]
    if (!runs.length) return minH

    let txt = runs
        .map((r) => r.text || '')
        .join('')
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    txt = txt.replace(/•\s*$/g, '').trim()
    if (!txt) return minH

    const parts = txt
        .split(/•\s*/g)
        .map((p) => p.replace(/\s+/g, ' ').trim())
        .filter((p) => p.length > 0)

    const lines = parts.reduce((sum, p) => sum + Math.max(1, Math.ceil(p.length / charsPerLine)), 0)
    const h = baseRowH + (lines - 1) * lineInc

    return Math.max(minH, Math.min(h, maxH))
}

export function drawTppTableTopBorderFull(slide, pptx, y, opts = {}) {
    const { slideW = 10.0, color = '2F3A4A', pt = 1 } = opts

    slide.addShape(pptx.ShapeType.line, {
        x: 0.0,
        y,
        w: slideW,
        h: 0,
        line: { color, pt },
    })
}

export function flushTppTable(slide, pptx, tableRows, rowHeights, opts = {}) {
    const { x, y, w, colW, fontSize, slideW = 10.0, borderColor = '2F3A4A', borderPt = 1, margin } = opts

    slide.addTable(tableRows, {
        x,
        y,
        w,
        colW,
        rowH: rowHeights,
        fontSize,
        border: { type: 'none' },
        ...(margin != null ? { margin } : {}),
    })

    drawTppTableTopBorderFull(slide, pptx, y, {
        slideW,
        color: borderColor,
        pt: borderPt,
    })
}

export function makeTppHeaderRow({ headers, tableFill, textColor = '000000', headerFontSize = null }) {
    const headerBorder = [{ color: 'FFFFFF' }, { color: 'FFFFFF' }, { color: '000000', pt: 2 }, { color: 'FFFFFF' }]

    return [
        headers.map((h, idx) => ({
            text: h || '',
            options: {
                fill: tableFill,
                color: textColor,
                align: idx === 0 ? 'left' : 'center',
                bold: true,
                border: headerBorder,
                ...(headerFontSize != null ? { fontSize: headerFontSize } : {}),
            },
        })),
    ]
}
